import React, { useState, useEffect, useRef } from "react";
import Header from "../components/header";
import "./home.css";

const SONG = {
  title: "كيفك إنت",
  en: " Kifak Inta",
  youtubeId: "nvr4lPoKqzI",
};

const TIMELINE_DATA = [
  { year: "1934",    event: "Born Nouhad Haddad",       detail: "In Zokak el-Blat, Beirut. Her father sang in church — her gift was hereditary." },
  { year: "1947",    event: "Named Fayrouz",             detail: "Broadcaster Halim Roumi gave her the name فيروز — Turquoise — seeing in her voice something rare and gemlike." },
  { year: "1951",    event: "Met the Rahbani Brothers",  detail: "At Lebanese Radio she met Assi and Mansour Rahbani, beginning the defining artistic partnership of Arab music." },
  { year: "1957",    event: "Baalbeck Festival Debut",   detail: "Her performance at the ancient Roman ruins electrified the Arab world and put Lebanon on the cultural map." },
  { year: "1967",    event: "Zahrat Al-Madaen",          detail: "Written for Jerusalem after the Six-Day War — the most beloved expression of longing for the holy city." },
  { year: "1975–90", event: "Voice Through Civil War",   detail: "While Lebanon burned, her recordings played on both sides of the Green Line — a shared heartbeat across the divide." },
  { year: "1994",    event: "Return to Beirut",          detail: "Her post-war return concert symbolised the city's own resurrection. Lebanon wept and celebrated as one." },
  { year: "Today",   event: "Living Legend",             detail: "Lebanon's morning radio still begins with Fayrouz. A tradition as old as independence itself." },
];

const SONGS_DATA = [
  { ar: "زهرة المدائن",      en: "Zahrat Al-Madaen",       year: "1967" },
  { ar: "وعلى بيروت",        en: "On Beirut",               year: "1983" },
  { ar: "كيفك إنت",          en: "Kifak Inta",              year: "1972" },
  { ar: "يا أنا يا أنا",    en: "Ya Ana Ya Ana",           year: "1969" },
  { ar: "شتي يا دني",        en: "Rain, Oh World",          year: "1960" },
  { ar: "أنا لحبيبي",        en: "I Am for My Beloved",     year: "1973" },
  { ar: "لما بتطفي الشموع", en: "When Candles Extinguish", year: "1966" },
  { ar: "بعيون رامية",       en: "With Shooting Eyes",      year: "1958" },
];


function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}


function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}


function MusicPlayer() {
  const [open, setOpen] = useState(true);
  return (
    <div className="player">
      <div className="player-top">
        <div className="player-meta">
          <div className="player-tag">فيروز · Now Playing</div>
          <div className="player-ar">{SONG.title}</div>
          <div className="player-en">{SONG.en}</div>
        </div>
        <button className="player-btn" onClick={() => setOpen(o => !o)}>
          {open ? "−" : "+"}
        </button>
      </div>
      {open && (
        <div className="player-content">
          <div className="waveform">
            {[6, 13, 18, 10, 16, 8, 14, 19, 11, 7].map((h, i) => (
              <div key={i} className="wbar" style={{ "--h": `${h}px`, animationDelay: `${i * 0.07}s` }} />
            ))}
          </div>
          <div className="video">
            <iframe
              title="Fayrouz - Ya Ana Ya Ana"
              src={`https://www.youtube.com/embed/${SONG.youtubeId}?controls=1&rel=0&modestbranding=1`}
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}


function Hero() {
  return (
    <section className="hero" id="hero">
      <div className="hero-backdrop">
        <div className="ring r1" />
        <div className="ring r2" />
        <div className="ring r3" />
      </div>
      {["♩", "♪", "♫", "♬", "♩", "♪"].map((n, i) => (
        <span key={i} className={`note note-${i}`}>{n}</span>
      ))}
      <div className="portrait">
      </div>
      <div className="hero-body">
        <p className="eyebrow"><span className="eyebrow-bar" />Voice of the Orient</p>
        <h1 className="title-main">Fayrouz</h1>
        <p className="title-ar">فيروز</p>
        <p className="subtitle">The Turquoise — Lebanon's Eternal Songbird</p>
        <p className="dates">Born 1934 · Beirut, Lebanon</p>
        <div className="ornament">✦</div>
        <p className="desc">
          For over seven decades, her voice has risen like incense at dawn across the Arab world —
          a sound so pure it became the very breath of Lebanon, coloring mornings from Beirut to
          Baghdad, from Cairo to Casablanca.
        </p>
      </div>
      <div className="scroll">
        <div className="scroll-bar" />
        Scroll to explore
      </div>
    </section>
  );
}


function MarqueeBand() {
  const items = ["وعلى بيروت سلام", "كيفك إنت؟", "زهرة المدائن", "شتي يا دني شتي", "يا أنا يا أنا", "Voice of the Orient"];
  const doubled = [...items, ...items];
  return (
    <div className="marquee">
      <div className="marquee-inner">
        {doubled.map((t, i) => (
          <span key={i} className="marquee-text">{t}<span className="marquee-sep">✦</span></span>
        ))}
      </div>
    </div>
  );
}

function OriginsSection() {
  return (
    <section id="origins" className="section">
      <Reveal className="grid">
        <div className="visual visual-origins">
          <div className="badge top-right">١٩٣٤</div>
        </div>
        <div className="text">
          <span className="num">01</span>
          <p className="label">Origins</p>
          <h2 className="heading">Born in the<br /><em>Mountains of Lebanon</em></h2>
          <p className="body">Born Nouhad Haddad on November 21, 1934, in the Zokak el-Blat neighborhood of Beirut. Her extraordinary voice was discovered early — clear, crystalline, and impossibly precise.</p>
          <p className="body">Her stage name Fayrouz — meaning "turquoise" — was given by broadcaster Halim Roumi, who recognized in her something rare and gemlike: a voice that would outlast generations.</p>
          <div className="tags">
            {["Beirut", "1934", "Nouhad Haddad", "Christian Maronite"].map(t => <span key={t} className="tag">{t}</span>)}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function MusicSection() {
  return (
    <section id="music" className="section">
      <Reveal className="grid reverse">
        <div className="visual visual-music">
          <svg viewBox="0 0 400 480" xmlns="http://www.w3.org/2000/svg" className="visual-svg">
            <defs>
              <radialGradient id="mg1" cx="60%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#b85c6e" stopOpacity="0.2" /><stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>
            <rect width="400" height="480" fill="url(#mg1)" />
            <g opacity="0.2" transform="translate(40,150)">
              {[0,20,40,60,80].map(y => <line key={y} x1="0" y1={y} x2="320" y2={y} stroke="#c9a84c" strokeWidth="1" />)}
            </g>
            <g opacity="0.5" transform="translate(40,150)">
              <ellipse cx="60" cy="40" rx="10" ry="8" fill="#c9a84c" transform="rotate(-15,60,40)" />
              <line x1="70" y1="40" x2="70" y2="-20" stroke="#c9a84c" strokeWidth="1.5" />
              <ellipse cx="140" cy="20" rx="10" ry="8" fill="#c9a84c" transform="rotate(-15,140,20)" />
              <line x1="150" y1="20" x2="150" y2="-40" stroke="#c9a84c" strokeWidth="1.5" />
              <ellipse cx="220" cy="60" rx="10" ry="8" fill="#c9a84c" transform="rotate(-15,220,60)" />
              <line x1="230" y1="60" x2="230" y2="0" stroke="#c9a84c" strokeWidth="1.5" />
            </g>
          </svg>
          <div className="badge bottom-left">الأخوان رحباني</div>
        </div>
        <div className="text">
          <span className="num">02</span>
          <p className="label">The Rahbani Partnership</p>
          <h2 className="heading">A Voice Meets<br /><em>Its Music</em></h2>
          <p className="body">At the 1951 Baalbeck International Festival, fate intervened. Fayrouz's voice found its perfect vessel in the compositions of the Rahbani Brothers — Assi and Mansour — who transformed Lebanese folk music into something transcendent.</p>
          <p className="body">Together they created musicals, films, and hundreds of songs that wove classical Arabic maqam scales with Western harmonies — an entirely new Arabic musical language.</p>
          <div className="tags">
            {["Rahbani Brothers", "Baalbeck Festival", "Arabic Theatre", "Maqam"].map(t => <span key={t} className="tag">{t}</span>)}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function LegacySection() {
  return (
    <section id="legacy" className="section">
      <Reveal className="grid">
        <div className="visual visual-legacy">
          <svg viewBox="0 0 400 480" xmlns="http://www.w3.org/2000/svg" className="visual-svg">
            <defs>
              <radialGradient id="lg1" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#c9a84c" stopOpacity="0.14" /><stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>
            <rect width="400" height="480" fill="url(#lg1)" />
            {[40,80,130,180].map((r,i) => (
              <circle key={i} cx="200" cy="220" r={r} fill="none" stroke="#c9a84c" strokeWidth={0.8-i*0.15} opacity={0.28-i*0.04} strokeDasharray={i>0?"4 10":undefined} />
            ))}
            <text x="200" y="228" textAnchor="middle" fontSize="18" fill="#c9a84c" opacity="0.5">✦</text>
            {[[310,140],[90,120],[340,280],[60,300],[230,360]].map(([cx,cy],i) => (
              <g key={i}>
                <circle cx={cx} cy={cy} r="3" fill="#c9a84c" opacity="0.3" />
                <line x1="200" y1="220" x2={cx} y2={cy} stroke="#c9a84c" strokeWidth="0.4" opacity="0.12" />
              </g>
            ))}
          </svg>
          <div className="badge top-right">∞</div>
        </div>
        <div className="text">
          <span className="num">03</span>
          <p className="label">Enduring Legacy</p>
          <h2 className="heading">The Voice That<br /><em>Carried a Nation</em></h2>
          <p className="body">During the Lebanese Civil War, Fayrouz's recordings played across the dividing lines — both sides listening to the same voice in the dark. She became more than a singer; she became Lebanon's wounded heart still beating.</p>
          <p className="body">With over 1,500 songs, 30 theatrical works, and 10 films, her art transcends generations. "Zahrat al-Madaen" became the unofficial anthem of Palestinian longing.</p>
          <div className="tags">
            {["1,500+ Songs", "Pan-Arab Icon", "Civil War Symbol", "Jerusalem"].map(t => <span key={t} className="tag">{t}</span>)}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function SongsSection() {
  return (
    <section id="songs" className="songs">
      <Reveal className="songs-head">
        <p className="label">Selected Works</p>
        <h2 className="songs-heading">Songs That <em>Shaped the Century</em></h2>
      </Reveal>
      <Reveal delay={150} className="songs-list">
        {SONGS_DATA.map((s, i) => (
          <div key={i} className="song">
            <span className="song-arabic">{s.ar}</span>
            <span className="song-english">{s.en}</span>
            <span className="song-date">{s.year}</span>
          </div>
        ))}
      </Reveal>
    </section>
  );
}

function TimelineSection() {
  return (
    <section id="timeline" className="timeline">
      <Reveal className="timeline-head">
        <p className="label">Life in Time</p>
        <h2 className="songs-heading">A <em>Chronicle</em> of a Voice</h2>
      </Reveal>
      <div className="tl-list">
        {TIMELINE_DATA.map((item, i) => (
          <Reveal key={i} delay={i * 80} className="tl-item">
            <div className="tl-dot" />
            <p className="tl-year">{item.year}</p>
            <p className="tl-event">{item.event}</p>
            <p className="tl-detail">{item.detail}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="page">
      <Header />
      <Hero />
      <MarqueeBand />
      <OriginsSection />
      <MusicSection />
      <LegacySection />
      <SongsSection />
      <TimelineSection />

      <section className="quote">
        <Reveal>
          <span className="quote-mark">"</span>
          <p className="quote-text">
            We still have Lebanon — and Lebanon has Fayrouz. As long as her voice rises with the morning,
            the mountains remember they are still standing.
          </p>
          <p className="quote-source">— On Fayrouz, Lebanon's Cultural Soul</p>
        </Reveal>
      </section>

      <footer className="footer">
        <span className="footer-title">فيروز · Fayrouz</span>
        <span className="footer-copy">Voice of the Orient · Lebanon · Est. 1934</span>
        <span className="footer-copy">A Digital Tribute</span>
      </footer>

      <MusicPlayer />
    </div>
  );
}